// Staged Gantt changes live in this browser until Shaun sends them (P13).
// localStorage can throw or come back empty (private window, cleared data). Every access is guarded,
// and the page works without it: staged changes then last until the tab closes.

import type { StagedPlan } from "./gantt";

export const GANTT_STAGED_KEY = "gantt.staged.v1";

export interface GanttStored {
  version: 1;
  items: Record<string, StagedPlan>;
  /** Kept across a failed send so a retry reuses it and bee does not save the batch twice. */
  clientBatchId?: string;
}

const EMPTY: GanttStored = { version: 1, items: {} };

function validItem(v: unknown): v is StagedPlan {
  const s = v as StagedPlan;
  return (
    !!s &&
    typeof s.phaseId === "string" &&
    typeof s.hash === "string" &&
    typeof s.heading === "string" &&
    typeof s.raw === "string" &&
    !!s.from &&
    !!s.to &&
    typeof s.to.start === "string" &&
    typeof s.to.end === "string"
  );
}

export function loadGanttStaged(): GanttStored {
  try {
    const raw = localStorage.getItem(GANTT_STAGED_KEY);
    if (!raw) return { ...EMPTY, items: {} };
    const s = JSON.parse(raw) as GanttStored;
    if (!s || s.version !== 1 || typeof s.items !== "object" || s.items === null) {
      return { ...EMPTY, items: {} };
    }
    const items = Object.fromEntries(Object.entries(s.items).filter(([, v]) => validItem(v)));
    return { version: 1, items, clientBatchId: s.clientBatchId };
  } catch {
    return { ...EMPTY, items: {} };
  }
}

export function saveGanttStaged(state: GanttStored): boolean {
  try {
    localStorage.setItem(GANTT_STAGED_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}
